import React, { useState, useEffect, useRef } from 'react';
import { Card, Form, Input, Button, Select, message, Typography, Divider } from 'antd';
import { SendOutlined, ReloadOutlined, PlayCircleOutlined, StopOutlined } from '@ant-design/icons';
import api from '../api/auth';
import { TimeUtil } from '../utils/timeUtil';
import { handleApiError } from '../utils/errorHandler';

const { Title, Text } = Typography;

interface Server {
  id: number;
  deviceId: string;
  remark?: string;
  lineStatus?: string;
  manageIp?: string;
}

interface MqttServer {
  id: number;
  name: string;
  url: string;
  port: number;
  username: string;
  password: string;
  useTls: boolean;
}

const CommandTest: React.FC = () => {
  const [servers, setServers] = useState<Server[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [form] = Form.useForm();
  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [mqttServer, setMqttServer] = useState<MqttServer | null>(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const [isListening, setIsListening] = useState<boolean>(false);
  const [responseOutput, setResponseOutput] = useState<string>('');
  const responseRef = useRef<HTMLDivElement>(null);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    fetchServers();
    fetchDefaultMqttServer();
    
    // 设置定时器，每30秒刷新一次服务器列表
    const intervalId = setInterval(() => {
      fetchServers();
    }, 30000);
    
    // 组件卸载时清除定时器
    return () => {
      clearInterval(intervalId);
      // 清除轮询定时器
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  // 当有新的响应输出时，自动滚动到底部
  useEffect(() => {
    if (responseRef.current) {
      responseRef.current.scrollTop = responseRef.current.scrollHeight;
    }
  }, [responseOutput]);
  
  const fetchServers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/servers', { params: { status: 'online' } });
      
      // 转换响应数据格式以匹配组件需要的格式
      const serverList = response.data.map((server: any) => ({
        id: server.id,
        deviceId: server.deviceId,
        remark: server.remark,
        lineStatus: 'online', // 因为只获取在线设备，所以直接设置为online
        manageIp: server.manageIp
      }));
      
      setServers(serverList);
    } catch (error) {
      handleApiError(error, '获取服务器列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchDefaultMqttServer = async () => {
    try {
      // 使用默认MQTT服务器配置
      const defaultMqttServer = {
        id: 1,
        name: '默认MQTT服务器',
        url: '125.65.79.28',
        port: 1883,
        username: 'wyos',
        password: 'wyos2025',
        useTls: false
      };
      setMqttServer(defaultMqttServer);
    } catch (error) {
      handleApiError(error, '设置默认MQTT服务器失败');
    }
  };
  
  // 轮询获取客户端响应
  const pollClientResponse = async () => {
    if (!selectedDeviceId) return;
    
    try {
      const response = await api.get(`/mqtt/response/${selectedDeviceId}`);
      const { success, responses } = response.data;
      
      if (success && responses && responses.length > 0) {
        for (const resp of responses) {
          setResponseOutput(prev => prev + `[${TimeUtil.formatServerTime(TimeUtil.now().toDate(), 'HH:mm:ss')}] 收到响应: ${resp}\n────────────────────────────────────────\n`);
        }
      }
    } catch (error: any) {
      // 静默处理错误，不显示在界面上
      console.error('获取客户端响应失败:', error);
    }
  };

  const startListening = () => {
    if (!selectedDeviceId || !mqttServer) {
      message.error('请先选择设备和确保MQTT服务器配置正确');
      return;
    }

    setIsListening(true);
    setResponseOutput('正在监听命令响应...\n');

    // 构建监听命令（仅用于显示）
    const listenCommand = `mosquitto_sub -h ${mqttServer.url} -p ${mqttServer.port} -u ${mqttServer.username} -P ${mqttServer.password} -t "client/${selectedDeviceId}/response"`;
    console.log('开始监听命令:', listenCommand);
    
    setResponseOutput(prev => prev + `已连接到MQTT服务器，准备接收命令响应...\n`);
    
    // 开始轮询获取客户端响应
    const interval = setInterval(pollClientResponse, 2000); // 每2秒轮询一次
    setPollingInterval(interval);
  };

  const stopListening = () => {
    setIsListening(false);
    
    // 停止轮询
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    
    setResponseOutput(prev => prev + `[${TimeUtil.formatServerTime(TimeUtil.now().toDate(), 'HH:mm:ss')}] 停止监听命令响应\n`);
  };

  const handleDeviceChange = (deviceId: string) => {
    setSelectedDeviceId(deviceId);
    
    // 如果正在监听，先停止监听，然后重新开始监听新设备
    if (isListening) {
      stopListening();
      setTimeout(() => {
        startListening();
      }, 500);
    }
  };

  const handleSendCommand = async (values: any) => {
    if (!selectedDeviceId || !mqttServer) {
      message.error('请先选择设备和确保MQTT服务器配置正确');
      return;
    }

    try {
      setSending(true);
      
      const command = values.command.trim();
      if (!command) {
        message.error('请输入要执行的命令');
        setSending(false);
        return;
      }
      
      // 添加到历史记录
      setCommandHistory(prev => [command, ...prev]);
      
      // 如果正在监听，添加到响应输出
      if (isListening) {
        setResponseOutput(prev => prev + `> 发送命令: ${command}\n`);
      }
      
      // 使用API发送命令
      const response = await api.post(`/mqtt/command/${selectedDeviceId}`, { command });
      
      if (response.data.success) {
        message.success('命令发送成功');
        
        // 如果正在监听，提示等待响应
        if (isListening) {
          setResponseOutput(prev => prev + `命令 "${command}" 已发送，等待客户端响应...\n`);
        }
      } else {
        message.error(`发送命令失败: ${response.data.message}`);
        setResponseOutput(prev => prev + `发送命令失败: ${response.data.message}\n`);
      }
    } catch (error: any) {
      handleApiError(error, '发送命令失败');
      setResponseOutput(prev => prev + `发送命令失败: ${error.message || '未知错误'}\n`);
    } finally {
      setSending(false);
    }
  };


  return (
    <div>
      <Title level={2}>MQTT命令测试</Title>
                
      <Card 
        title="选择设备" 
        extra={<Button icon={<ReloadOutlined />} onClick={fetchServers}>刷新设备列表</Button>}
        style={{ marginBottom: 16 }}
      >
        <Form layout="vertical">
          <Form.Item
            label="设备ID"
            required
          >
            <Select 
              placeholder="请选择设备" 
              loading={loading}
              showSearch
              optionFilterProp="children"
              value={selectedDeviceId}
              onChange={handleDeviceChange}
              style={{ width: '100%' }}
            >
              {servers.map(server => (
                <Select.Option key={server.deviceId} value={server.deviceId}>
                  {server.remark || server.deviceId} ({server.deviceId}) 
                  {server.lineStatus && ` - ${server.lineStatus === 'online' ? '在线' : '离线'}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Card>
      
      <Card 
        title="监听命令响应" 
        extra={
          isListening ? (
            <Button 
              type="primary" 
              danger 
              icon={<StopOutlined />} 
              onClick={stopListening}
            >
              停止监听
            </Button>
          ) : (
            <Button 
              type="primary" 
              icon={<PlayCircleOutlined />} 
              onClick={startListening}
              disabled={!selectedDeviceId || !mqttServer}
            >
              开始监听
            </Button>
          )
        }
        style={{ marginBottom: 16 }}
      >
        <div 
          ref={responseRef}
          style={{ 
            backgroundColor: '#000', 
            color: '#fff', 
            padding: '10px', 
            borderRadius: '4px', 
            height: '300px', 
            overflowY: 'auto',
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            overflowX: 'auto'
          }}
        >
          {responseOutput || '选择设备并点击"开始监听"按钮开始监听命令响应'}
        </div>
        <div style={{ marginTop: '10px', color: '#888' }}>
          <Text type="secondary">
            监听命令: {mqttServer && selectedDeviceId ? 
              `mosquitto_sub -h ${mqttServer.url} -p ${mqttServer.port} -u ${mqttServer.username} -P ${mqttServer.password} -t "client/${selectedDeviceId}/response"` : 
              '请先选择设备'}
          </Text>
        </div>
      </Card>
      
      <Card title="发送命令">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSendCommand}
        >
          <Form.Item
            name="command"
            label="命令"
            rules={[{ required: true, message: '请输入要执行的命令' }]}
          >
            <Input placeholder="例如: ls -l" />
          </Form.Item>
          
          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              icon={<SendOutlined />}
              loading={sending}
              disabled={!selectedDeviceId || !mqttServer}
            >
              发送命令
            </Button>
          </Form.Item>
        </Form>
        
        <div style={{ marginTop: '10px', color: '#888' }}>
          <Text type="secondary">
            发送命令: {mqttServer && selectedDeviceId ? 
              `mosquitto_pub -h ${mqttServer.url} -p ${mqttServer.port} -u ${mqttServer.username} -P ${mqttServer.password} -t "client/${selectedDeviceId}/command" -m "您输入的命令"` : 
              '请先选择设备'}
          </Text>
        </div>
      </Card>
      
      <Divider />
      
      <Card title="命令历史">
        {commandHistory.length > 0 ? (
          <div>
            {commandHistory.map((command, index) => (
              <div key={index} style={{ marginBottom: 8 }}>
                <Text code>{command}</Text>
                <Button 
                  type="link" 
                  size="small"
                  onClick={() => {
                    form.setFieldsValue({ command });
                  }}
                >
                  重用
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <Text>暂无命令历史</Text>
        )}
      </Card>
    </div>
  );
};

export default CommandTest;
